import { extractProtocolAndDomain } from '@/common/utils/extractProtocolAndDomain'
import { resolveRegistryElementXml } from '@/common/utils/resolveRegistryElementXml'
import { pickElementEventScript } from '@/common/utils/scriptNodeText'
import { setType } from '@/common/utils/typedValueSerializer'
import {
  extractEditableColumnNamesFromElement,
  type EditableRegistryElement,
} from '@/common/utils/editableTableOnChangeRetry'

export type EditableColumnMeta = {
  name: string
  isReadOnly: boolean
  type: string
}

function readNodeText(value: unknown): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const node = value as { _text?: string; value?: string }
    return String(node._text ?? node.value ?? '')
  }
  return ''
}

function readColumnName(column: unknown): string {
  if (!column || typeof column !== 'object') return ''
  const c = column as Record<string, unknown>
  const name = c.name
  if (typeof name === 'string') return name
  if (name && typeof name === 'object') {
    return readNodeText(name).trim()
  }
  const attrs = c._attributes as Record<string, unknown> | undefined
  return typeof attrs?.name === 'string' ? attrs.name : ''
}

export function extractEditableColumnMetaFromElement(elementLike: unknown): EditableColumnMeta[] {
  if (!elementLike || typeof elementLike !== 'object') return []
  const columns = (elementLike as { columns?: { column?: unknown } }).columns
  if (!columns) return []
  const raw = columns.column
  const arr = Array.isArray(raw) ? raw : raw ? [raw] : []
  return arr
    .map((column) => {
      const name = readColumnName(column)
      if (!name) return null
      const c = column as Record<string, unknown>
      const ro = readNodeText(c.isReadOnly).trim().toLowerCase()
      return {
        name,
        isReadOnly: ro === '1' || ro === 'true',
        type: readNodeText(c.type).trim() || 'String',
      }
    })
    .filter((item): item is EditableColumnMeta => item != null)
}

export function elementOnChangeUsesExtractProtocolAndDomain(elementLike: unknown): boolean {
  const script = pickElementEventScript(elementLike as Record<string, unknown>, 'onChange') ?? ''
  return script.includes('extractProtocolAndDomain')
}

function readRowColumnValue(row: unknown, columnName: string): unknown {
  if (!row || typeof row !== 'object') return undefined
  const r = row as Record<string, unknown>
  const fields = r.fields as Record<string, { value?: unknown }> | undefined
  const cell = fields?.[columnName]
  if (cell && typeof cell === 'object' && 'value' in cell) {
    return cell.value
  }
  return r[columnName]
}

function writeRowColumnValue(row: Record<string, unknown>, columnName: string, value: unknown): void {
  const fields =
    row.fields && typeof row.fields === 'object' && !Array.isArray(row.fields)
      ? (row.fields as Record<string, { value: unknown; type: string }>)
      : {}
  fields[columnName] = setType(value)
  row.fields = fields
}

function isEmptyCellValue(value: unknown): boolean {
  return value === '' || value === null || value === undefined
}

/** Вычисляет read-only url-колонку из protocol + text domain по onChange-паттерну meta. */
export function enrichEditableTableRowDerivedUrl(
  row: unknown,
  columns: readonly EditableColumnMeta[],
): boolean {
  if (!row || typeof row !== 'object') return false

  const readOnlyCols = columns.filter((c) => c.isReadOnly)
  const protocolCol = columns.find(
    (c) => !c.isReadOnly && c.type.toUpperCase() === 'LISTBOX',
  )
  const domainCol = columns.find(
    (c) =>
      !c.isReadOnly &&
      c !== protocolCol &&
      (c.type.toUpperCase() === 'TEXT' || c.type.toUpperCase() === 'STRING'),
  )
  const urlCol = readOnlyCols[0]

  if (!urlCol || !protocolCol || !domainCol) {
    return false
  }

  const currentUrl = readRowColumnValue(row, urlCol.name)
  if (!isEmptyCellValue(currentUrl)) return false

  const protocolRaw = readRowColumnValue(row, protocolCol.name)
  const domainRaw = readRowColumnValue(row, domainCol.name)
  if (isEmptyCellValue(domainRaw)) return false

  const protocolIn = String(protocolRaw ?? 'https')
  const domainIn = String(domainRaw)
  const result = extractProtocolAndDomain(protocolIn, domainIn)
  const domain = result.domain ?? ''
  const protocol = result.protocol ?? protocolIn
  const url = domain.trim().length > 0 ? `${protocol}://${domain}` : ''

  const mutableRow = row as Record<string, unknown>
  writeRowColumnValue(mutableRow, urlCol.name, url)
  writeRowColumnValue(mutableRow, protocolCol.name, protocol)
  if (domain && domain !== domainIn) {
    writeRowColumnValue(mutableRow, domainCol.name, domain)
  }
  return true
}

export function enrichEditableTableUrlFieldsInClientDataFields(
  clientDataFields: Record<string, unknown>,
  elements: Record<string, EditableRegistryElement | null | undefined>,
  formId?: string | number,
): string[] {
  const wanted =
    formId != null && formId !== '' && formId !== -1 ? String(formId) : null
  const changedTables: string[] = []

  for (const reg of Object.values(elements)) {
    if (!reg || String(reg.elementType ?? '').toUpperCase() !== 'EDITABLE') continue
    if (wanted && String(reg.formId ?? '') !== wanted) continue

    const fieldName = reg.fieldName ?? ''
    if (!fieldName) continue

    const el = resolveRegistryElementXml(reg)
    if (!el) continue
    if (!elementOnChangeUsesExtractProtocolAndDomain(el)) continue

    const tableEntry = clientDataFields[fieldName] as
      | { type?: string; value?: { rows?: unknown[] } }
      | undefined
    if (!tableEntry) continue
    const rows = tableEntry?.value?.rows
    if (!Array.isArray(rows) || rows.length === 0) continue

    const columnMeta = extractEditableColumnMetaFromElement(el)
    if (!columnMeta.length) continue

    let rowChanged = false
    for (const row of rows) {
      if (enrichEditableTableRowDerivedUrl(row, columnMeta)) {
        rowChanged = true
      }
    }

    if (rowChanged) {
      changedTables.push(fieldName)
      if (tableEntry && tableEntry.value) {
        clientDataFields[fieldName] = {
          type: tableEntry.type ?? 'UniDataTable',
          value: tableEntry.value,
        }
      }
    }
  }

  return changedTables
}
